class Localization {
	constructor(locale, fallbackLocale) {
		this.setFallbackLocale(fallbackLocale);
		this.locale = this.setLocale(locale);

		this.i18n = {};
	}

	load() {
		const locale = this.getLocale();
		const fallbackLocale = this.getFallbackLocale();
		if (this.dictionaryPromise) {
			return this.dictionaryPromise;
		}

		this.loaded = true;

		this.dictionaryPromise = new Promise((resolve) => {
			// Make sure to lowercase the locale when loading the file
			const localePromise = import(/* webpackChunkName: "i18n.[index]" */ `../i18n/${locale.toLowerCase()}.json`)
				.then((dictionary) => {
					this.i18n[locale] = dictionary;
				}).catch(() => { });

			// Once again, lowercase the locale when loading the file
			const fallbackLocalePromise = import(/* webpackChunkName: "i18n.[index]" */ `../i18n/${fallbackLocale.toLowerCase()}.json`)
				.then((dictionary) => {
					this.i18n[fallbackLocale] = dictionary;
				}).catch(() => { });

			Promise.all([localePromise, fallbackLocalePromise]).then(() => {
				resolve();
			});
		});

		return this.dictionaryPromise;
	}

	setFallbackLocale(locale) {
		this.fallbackLocale = this.isValidLocale(locale) ? locale : 'en';
	}

	getFallbackLocale() {
		return this.fallbackLocale;
	}

	setLocale(locale) {
		// These locales map to other values
		const localeMap = {
			'es-MX': 'es-xl'
		};

		const finalLocale = localeMap[locale] || locale;

		if (this.isValidLocale(finalLocale)) {
			return finalLocale;
		}

		if (this.isValidLocale(finalLocale.split('-')[0])) {
			return finalLocale.split('-')[0];
		}

		return this.fallbackLocale;
	}

	getLocale() {
		return this.locale;
	}

	setValidLocales(addLocale) {
		const defaults = ['de', 'en', 'es-XL', 'es', 'fr-CA', 'fr', 'it', 'ja', 'pt', 'qaT'];

		if (typeof addLocale !== 'undefined' && !!addLocale) {
			defaults.push(addLocale);
		}

		this.validLocales = defaults;

		return this.validLocales;
	}

	getValidLocales() {
		return this.validLocales || this.setValidLocales();
	}

	isValidLocale(locale) {
		return this.getValidLocales().indexOf(locale) >= 0;
	}

	getDictionary() {
		return this.i18n[this.getLocale()];
	}

	getFallbackDictionary() {
		return this.i18n[this.getFallbackLocale()];
	}

	/**
	 * Finds the specified key (or a fallback value from lastChance), then replaces any placeholders from an object
	 * @param key {string} A key that is used to look up the localized string
	 * @param data {Object=} An optional key:value object that is used for string replacements. "{{key}}" is replaced by "value"
	 * @param lastChance {(function():string|string)=} An optional string or function that will be used or called if the key was not found
	 * @return {string}
	 */
	get(key, data, lastChance) {
		let value = this.findValue(key, lastChance) || '';

		if (data && typeof data === 'object') {
			Object.keys(data).forEach((dataKey) => {
				value = value.replace(`{{${dataKey}}}`, data[dataKey]);
			});
		}

		return value;
	}

	findValue(key, lastChance) {
		const appKey = `cloud-jb-event.${key}`;
		const keyLocaleResult = this.getDictionary()[appKey];
		const keyFallbackResult = this.getFallbackDictionary()[appKey];
		const keyResult = keyLocaleResult || keyFallbackResult;
		const defaultFail = (key2) => `[${key2}]`;
		const notFound = lastChance || defaultFail;

		if (typeof keyResult !== 'undefined') {
			return keyResult;
		}

		return (typeof notFound === 'function') ? notFound(key) : notFound;
	}
}

export default function (props = {}) {
	const { locale = 'en', fallbackLocale = 'en' } = props;

	return new Localization(locale, fallbackLocale);
}
